import type {AiMetrics} from "utils/aiScore";
import axios, {AxiosError} from 'axios'
import {BACKEND_URL, SECRET_KEY} from "config";
import {IUserSettings, PlagiarismData, PlanPeriod, Sentence} from './types';
import {getClientIdFromCookie, sleep} from "./utils";
import {rootStore} from "stores/rootStore";
import type {AppBillingReconcile} from "types";
import {retryNetwork} from 'utilsjs';
import {getUniqueToken} from "./persistence";
import {getLanguage} from "i18n/LanguageProvider";
import {hmacSha256Hex, bytesToHex} from "./lib/cryptoFallback";
import {getClientCountry} from "./lib/clientCountry";
import {measurementId} from './utils/funnel';
import {deliverEvent, postEvent} from './utils/eventDelivery';
import type {ABArms, ServerArms} from './utils/abTest';

const skipErrorPaths = ['/utm'];


// TODO: handle errors with try, catch where needed instead of global
axios.interceptors.response.use(
    response => response,
    function (error) {
        const url = error?.config?.url || '';

        if (skipErrorPaths.some(path => url.includes(path))) {
            return Promise.reject(error);
        }

        // rootStore.setError(reprNetworkError(error));
        return Promise.reject(error);
    }
);

const request = async (
    endpoint: string,
    data: object,
    method = "post",
    retry = false,
    signal?: AbortSignal,
    timeout?: number
) => {
    const headers: Record<string, string> = {
        'Authorization': 'Token ' + rootStore.user.token
    }
    // A hint, not a claim: the backend decides what it's worth (it never
    // unlocks market pricing on its own).
    const country = getClientCountry()
    if (country) headers['X-Client-Country'] = country
    const url = BACKEND_URL + endpoint
    let response = null
    if (retry) {
        response = await retryNetwork(axios.request, {
            url,
            method,
            headers,
            data,
            signal,
            timeout
        })
    } else {
        response = await axios.request({
            url,
            method,
            headers,
            data,
            signal,
            timeout
        })
    }
    return response.data
}

// Boot requests (the guest session, the first /users/me) are what the page waits on. A hung
// backend worker accepts and never answers, so each is cut short and retried while nothing
// answered: at most 20 s per request, and the pauses ride out a brief network loss or a 502.
export const BOOT_TIMEOUT_MS = 5_000;
export const BOOT_RETRY_DELAYS_MS = [2_000, 3_000];

/** No working backend answered: a timeout, a network error, or a proxy replying in its place. */
export const isUnreachable = (e: unknown) =>
    axios.isAxiosError(e) && (!e.response || e.response.status >= 502);

// A failed task is answered with a coded 503: that is the verdict, not a dead hop.
const isDeadHop = (e: unknown) => isUnreachable(e) && !(e as any).response?.data?.error?.code;

/** The backend answered that it no longer knows this token. */
export const isTokenRejected = (e: unknown) =>
    axios.isAxiosError(e) && e.response?.status === 401;

// A proxy or captcha page answers 200 with HTML, which axios hands over as a string. Thrown
// with no response attached, so isUnreachable() takes it for what it is: no backend answered.
const requireJson = <T>(data: T, endpoint: string): T => {
    if (typeof data !== 'object' || data === null) throw new AxiosError(`${endpoint} answered without JSON`, AxiosError.ERR_BAD_RESPONSE)
    return data
}

/** One attempt per delay, then a last one whose failure is the verdict. */
export const retryWhileUnreachable = async <T>(
    fn: () => Promise<T>, delays = BOOT_RETRY_DELAYS_MS, shouldRetry = isUnreachable,
): Promise<T> => {
    for (const delay of delays) {
        try {
            return await fn();
        } catch (e) {
            if (!shouldRetry(e)) throw e;
            await sleep(delay);
        }
    }
    return await fn();
}


// Fetch text handed off from a landing page via a one-time token (see
// bitapi/analysis/share_text.py). The token is consumed server-side on read.
export const getSharedText = async (token: string): Promise<string> => {
    const data = await request(`share-text/${token}`, {}, "get")
    return data.text
}

// Fetch files handed off from a landing page via a one-time token (see
// bitapi/analysis/share_files.py). Returns lightweight upload-widget metadata
// (no text — the scan endpoint reads it server-side by uuid). Token consumed
// on read.
export const getSharedFiles = async (token: string): Promise<IUploadFileResponse[]> => {
    const data = await request(`share-files/${token}`, {}, "get")
    return data.files
}

// Cello referral widget boot config: a backend-signed JWT + productId, or
// {enabled: false} when the integration is off for this env/account.
export const getCelloToken = async (): Promise<{ enabled: boolean, token?: string, product_id?: string }> => {
    return await request(`users/cello-token/`, {}, "get")
}

// Attach the referral code a fresh signup arrived with (set-once server-side).
export const sendCelloAttribution = async (ucc: string) => {
    return await request(`users/cello-attribution/`, {ucc})
}

// The referral discount this account gets at checkout. Its own endpoint rather
// than a /users/me field: answering it means asking Cello.
export const getReferralDiscount = async (): Promise<{ eligible: boolean, percent_off?: number, months?: number }> => {
    return await request(`users/referral-discount/`, {}, "get")
}

export type PromoOffer = { code: string, percent_off: number, period: '' | PlanPeriod };

// What a promotion code from a letter takes off, so plan cards can quote the
// price checkout will charge. Checkout re-checks the code; this only quotes it.
export const getPromoOffer = async (code: string): Promise<PromoOffer | null> => {
    const data = await request(`payments/promo?code=${encodeURIComponent(code)}`, {}, "get");
    return data?.valid ? {code: data.code, percent_off: data.percent_off, period: data.period || ''} : null;
}

// A poll reads the task's current state, so an answer that takes seconds is a dead
// connection: cut it short and ask again. Only a run of such misses ends the wait.
const POLL_TIMEOUT_MS = 10_000;
const POLL_RETRY_DELAY_MS = 2_000;
const POLL_MAX_MISSES = 6;

const poolUntil = async (url: string, isReady: (data: any) => boolean, signal?: AbortSignal, maxTries: number = 20,
                         onPoll?: (data: any) => void, pollIntervalMs?: number): Promise<any | null> => {
    const intervals = [0.05, 0.1, 0.1, 0.5, 1, 1, 2, 2, 2, 4, 4, 4, 4];
    let misses = 0;
    while (true) {
        try {
            const data = await request(url, {}, "get", false, signal, POLL_TIMEOUT_MS);
            misses = 0;
            if (isReady(data)) {
                return data
            }
            onPoll?.(data);
        } catch (e) {
            if (signal?.aborted || !isDeadHop(e) || ++misses >= POLL_MAX_MISSES) throw e;
        }
        if (signal?.aborted) {
            break
        }
        maxTries -= 1;
        if (maxTries === 0) {
            break
        }

        const interval = pollIntervalMs ?? ((intervals.shift() || 6) * 1000);
        await sleep(misses ? Math.max(interval, POLL_RETRY_DELAY_MS) : interval)
    }
    return null
}

interface IScanResponse {
    existing?: boolean;
    "analysis_id": number;
    "analysis_uuid": string;
    "ai_task_id": number;
    "plagiarism_task_id": number;
    "grammar_task_id"?: number;
}

export const scanRequest = async (
    text: string | null = null,
    uuid: string | null = null,
    scanFlag: boolean = false,
    deepScanFlag: boolean = false,
    plagiarismFlag: boolean = false,
    grammarFlag: boolean = false,
    source: string = "unknown",
    signal?: AbortSignal,
    // Ties the analysis to an uploaded document when the text is sent inline instead of being
    // read from `uuid` — the "scan only the first N chars of this file" offer. Passing `uuid`
    // makes the backend use the WHOLE document, so a prefix run has to send the text itself;
    // doc_id keeps the analysis recorded against the file all the same (the backend stores
    // `uuid` as doc_id, so both paths land the same way).
    docId: string | null = null,
    // The one-time scan run this scan spends: the plan's limits don't apply to it, and every
    // file of one batch carries the same id so the batch spends one purchase.
    oneTimeRun: string | null = null,
): Promise<IScanResponse> => {
    return await request(
        "scan",
        {'text': text, "deep_scan": deepScanFlag, "source": source,
            "ai_check": scanFlag, "plagiarism_check": plagiarismFlag,
            "grammar_check": grammarFlag, "uuid": uuid,
            ...(docId ? {"doc_id": docId} : {}),
            ...(oneTimeRun ? {"one_time_scan_run": oneTimeRun} : {})},
        "post", false, signal)
};

// 120 tries ≈ 667s (24.75s of ramp-up, then 6s steps).
const TASK_RESULT_MAX_TRIES = 120;
// ≈ 27 min. An AI scan waits as long as the ML service reports its job queued or running,
// and the backend ends that wait itself at ML_SCAN_MAX_WAIT (25 min,
// app/config/configuration.py) — failing the scan and refunding its words — so the client
// has to still be polling when that verdict lands.
export const AI_TASK_RESULT_MAX_TRIES = 280;

/** Where a waiting AI scan stands in the ML service (`ml` of a task poll). */
export interface MlProgress {
    /** pending — queued behind other scans; processing — being scanned. */
    state: "pending" | "processing";
    /** Estimated share done, 0–1; never 1 before the result. */
    progress: number;
    eta_seconds: number;
}

export const waitTaskResult = async (task_id: number, signal?: AbortSignal, maxTries = TASK_RESULT_MAX_TRIES,
                                     onPoll?: (task: any) => void, pollIntervalMs?: number) => {
    const statusResponse = await poolUntil(`tasks/${task_id}`,
        (data: any) => (data.status === "success" || data.status === "error"), signal, maxTries, onPoll, pollIntervalMs)

    if (statusResponse === null) {
        throw new Error("Request timed out")
    }
    return statusResponse
};

// export const retryTask = await (task_id) => {
//     return await request(`tasks/${task_id}/retry`, {}, "post")
// }

const isAuthenticated = () => {
    return rootStore.user.token !== null;
}


export const createAPIKey = async (name: string) => {
    if (!name.length) name = '-';
    return await request("key", {name: name})
}

export const getApiKeys = async () => {
    return await request("key", {}, "get")
}

export const deleteAPIKey = async (id: number) => {
    return await request(`key/${id}`, {}, "delete")
}

export const updateAPIKey = async (id: number, title: string) => {
    return await request(`key/${id}`, {name: title}, "patch")
}

/** `boot`: the page is waiting on this answer, so it is bounded and retried (see BOOT_TIMEOUT_MS). */
export const getMe = async (boot = false): Promise<IUserSettings> => {
    const fetchMe = async (timeout?: number) =>
        requireJson(await request('users/me/', {}, "get", false, undefined, timeout), 'users/me')
    if (!boot) return await fetchMe()
    return await retryWhileUnreachable(() => fetchMe(BOOT_TIMEOUT_MS))
}

export const registerHumanizerEntry = async (source_page: string) =>
    request('users/humanizer-entry/', {source_page}, 'post', true);

/** Store the UI language on the account; a no-op when it is already the stored one. */
export const setAccountLocaleRequest = async (locale: string) => {
    return await request("users/locale/", {locale}, "post", true);
}

/**
 * Keep the arms this browser rolled on the account (the first per test wins; `force` moves the
 * account to them); returns the account's arms.
 */
export const keepABArmsRequest = async (arms: ABArms, force = false): Promise<ABArms> => {
    const {ab_arms} = await request("users/ab-arms/", {arms, force}, "post", true);
    return ab_arms;
}

/** Whether "Scan once" charges the saved card straight away (`skip`) or asks first. */
export const setOneTimeConfirmRequest = async (skip: boolean) => {
    return await request("users/one_time_confirm/", {skip}, "post", true);
}

export const getAnalytics = async (trashold: number) => {
    return await request('analitycs', {trashold})
}

/**
 * @deprecated Do not call — POST /subscribe-request answers 410 since Aug 2026.
 * `is_free: true` used to grant the caller a free Premium (anyone with a token,
 * anons included), and the sales-lead half it shared the path with has moved to
 * Calendly. Kept only so the retired contract stays visible next to the others;
 * nothing in the app calls it.
 */
export const sendSubscribeRequest = async (name: string, role_name: string) => {
    return await request('subscribe-request', {is_free: false, name, role_name})
}

export const sendEventRequest = async (event: string, options = {}) => {
    if (!isAuthenticated()) {
        console.warn("Send event without auth", event, options)
        return
    }
    const data = JSON.stringify({event, options, client_event_id: measurementId(),
        client_id: getClientIdFromCookie(), service: "app"});
    const headers: Record<string, string> = {
        Authorization: 'Token ' + rootStore.user.token,
        'Content-Type': 'application/json',
    };
    const country = getClientCountry();
    if (country) headers['X-Client-Country'] = country;
    return deliverEvent(() => postEvent(BACKEND_URL + 'events', data, headers));
}

export const getHistoryRequest = async (page: number, pageSize: number) => {
    return await request('history?page=' + page + "&page_size=" + pageSize, {}, "get", true)
}

/** A scan of one's history in full — the list carries only a preview of each. */
export interface HistoryAnalysisResponse {
    analysis_uuid: string
    date: string
    /** Also there when no check finished: such a scan comes back as its text alone. */
    text: string
    /** Its finished checks, as the report carries them. */
    items: ScanResponse[]
}

export const getHistoryAnalysis = async (uuid: string): Promise<HistoryAnalysisResponse> => {
    return await request(`history/${uuid}`, {}, "get", true)
}

/** A scan's whole text alone, without the results only Restore needs. */
export const getHistoryText = async (uuid: string): Promise<{text: string}> => {
    return await request(`history/${uuid}/text`, {}, "get", true)
}

export interface IHumanizeResponse {
    analysis_id: number;
    analysis_uuid: string;
    /** Poll on tasks/<id>; its success data carries humanized_text + the result scan's ids. */
    humanizer_task_id: number;
    /** The free deep AI scan of the ORIGINAL text; null when the run reused the verdict
     *  named by `sourceAnalysisUuid`. */
    ai_task_id: number | null;
}

/** `sourceAnalysisUuid`: a previous run's original, whose verdict already describes this
 *  exact text — the backend attaches the run to it instead of scanning the original again. */
export const humanizeRequest = async (text: string, mode: string, sourceAnalysisUuid: string | null = null): Promise<IHumanizeResponse> => {
    return await request("humanize", {text, mode, source_analysis_uuid: sourceAnalysisUuid})
}

/** The rewrite attempt a waiting Humanizer run is on (`humanizer_attempt` of a task poll). */
export interface HumanizerAttempt {
    number: number;
    max: number;
    /** Share of this attempt's rewrite streamed in so far, 0–1; absent or null when unknown. */
    progress?: number | null;
}

/** An AI verdict of a Humanizer run as /humanizer/history reports it; null = never scanned. */
export type HumanizerVerdictStatus = 'processing' | 'success' | 'error' | null;

export interface HumanizerHistoryRow {
    id: number;
    date_created: string;
    /** A failed run has no humanized text and no result analysis. */
    status: 'success' | 'error';
    mode: string;
    original_text: string;
    humanized_text: string | null;
    words_count: number | null;
    original_ai_status: HumanizerVerdictStatus;
    original_ai_score: number | null;
    humanized_ai_status: HumanizerVerdictStatus;
    humanized_ai_score: number | null;
    original_analysis_uuid: string;
    result_analysis_uuid: string | null;
}

export const getHumanizerHistoryRequest = async (page: number, pageSize: number):
        Promise<{results: HumanizerHistoryRow[], count: number}> => {
    return await request('humanizer/history?page=' + page + "&page_size=" + pageSize, {}, "get", true)
}

export const getFeedback = async (id: string, feedback: 'positive' | 'negative') => {
    return await request(`feedback/${id}`, {feedback})
}

export const feedbackRequest = async (rejected?: boolean, rating?: number, message?: string) => {
    return await request("users/feedback/", {
        "rating": rating,
        "message": message,
        "rejected": rejected
    }, "post", true)
}

export const sendQuestionnaireRequest = async (
    workPlace: string,
    companyName: string,
    jobTitle: string,
    jobPosition: string,
    aiDetectorPurpose: string,
    currentSolution: string,
    discoverySource: string,
    otherTools: string,
    importantFeatures: string
) => {
    return await request("questionnaire/", {
        work_place: workPlace,
        company_name: companyName,
        job_title: jobTitle,
        job_position: jobPosition,
        ai_detector_purpose: aiDetectorPurpose,
        current_solution: currentSolution,
        discovery_source: discoverySource,
        other_tools: otherTools,
        important_features: importantFeatures
    })
}

export const completeOnboardingRequest = async () => {
    return request("users/complete_onboarding/", {}, "post", true)
}

/** Records on the account that it was shown this release announcement. */
export const announcementSeenRequest = async (id: string) => {
    return request("users/announcement-seen/", {id}, "post", true)
}

export const enrollUserRequest = async () => {
    return await request("users/enroll_user/", {}, "post", true);
}

export const markFirstLimitHitRequest = async () => {
    return await request("users/mark_first_limit_hit/", {}, "post", true);
}

export const socialMediaSubscribeRequest = async (source: string, linkUrl: string) => {
    return await request("social-media-subscribe/", {
        source: source,
        link_url: linkUrl
    })
}

export interface IUploadFileResponse {
    id: string;
    file_name: string;
    size: number;
    type: string;
    url: string;
    characters_count?: number;
    words_count?: number;
    is_english: boolean;
    file_size: string;
    uuid: string;
    text?: string;
    // 'processing' пока асинхронный OCR на бэке не дочитал документ.
    status?: 'ready' | 'processing' | 'failed';
    progress?: number;   // прогресс OCR 0–100 (пока status='processing')
}

/** Poll upload status (used while the backend OCR runs asynchronously). */
export const getUploadStatus = async (uuid: string): Promise<IUploadFileResponse> => {
    const response = await axios.get(BACKEND_URL + 'upload/' + uuid + '/', {
        headers: {'Authorization': 'Token ' + rootStore.user.token},
        timeout: 10000,
    });
    return response.data;
}

// A scanned PDF is read by OCR after the upload answers: polled at this pace, for this long.
const UPLOAD_POLL_MS = 2000;
const UPLOAD_POLL_ATTEMPTS = 90;   // ~3 minutes

/**
 * Wait for an upload that answered `processing` to finish its OCR. Resolves to the ready
 * (or failed) file. Giving up is no verdict on the document, so it throws: the last poll's
 * own error when that got no answer, else a timeout — the server is still reading it.
 * `onProgress` sees each intermediate status; `isAlive` lets the caller stop caring (a
 * cleared batch, a replaced pane) — the initial answer comes back then, untouched.
 */
export const pollUploadReady = async (
    uuid: string, initial: IUploadFileResponse,
    {onProgress, isAlive = () => true}: {onProgress?: (status: IUploadFileResponse) => void; isAlive?: () => boolean} = {},
): Promise<IUploadFileResponse> => {
    let lastError: unknown;
    for (let i = 0; i < UPLOAD_POLL_ATTEMPTS; i++) {
        await new Promise(resolve => setTimeout(resolve, UPLOAD_POLL_MS));
        if (!isAlive()) return initial;
        try {
            const status = await getUploadStatus(uuid);
            if (status.status && status.status !== 'processing') return status;
            lastError = undefined;
            onProgress?.(status);
        } catch (e) {
            // A transient network error: keep polling.
            lastError = e;
        }
    }
    throw lastError ?? new Error('Document still processing');
}

// Pauses before re-sending an upload whose connection dropped: a phone back from the
// background or a flaky link needs a moment, not an instant retry.
const UPLOAD_RETRY_DELAYS_MS = [1000, 3000];

/** Upload a document. A dropped connection is retried with the same uuid: the backend
 *  answers a repeat of an upload it already stored with that file. */
export const uploadFile = async (file: File, uuid: string, onProgress?: (progress: number) => void): Promise<IUploadFileResponse | { error: string }> =>
    retryWhileUnreachable(() => postUpload(file, uuid, onProgress), UPLOAD_RETRY_DELAYS_MS, isDeadHop);

const postUpload = async (file: File, uuid: string, onProgress?: (progress: number) => void) => {
    const bodyFormData = new FormData();
    bodyFormData.append('file', file);
    bodyFormData.append('uuid', uuid);
    const response = await axios.post(
        BACKEND_URL + 'upload/',
        bodyFormData,
        {
            headers: {
                'Authorization': 'Token ' + rootStore.user.token,
                "Content-Type": "multipart/form-data"
            },
            maxBodyLength: Infinity,
            onUploadProgress: (progressEvent) => {
                const total = progressEvent.total || file.size;
                const loaded = progressEvent.loaded;
                const progress = total > 0 ? Math.round((loaded / total) * 100) : 0;
                onProgress?.(progress);
            }
        }
    );
    return response.data;
}


export const createStripeSession = async (type: string, period: string | null = null, preview = false, discount: number = 0, trial: boolean = false, promo?: string, analyticsContext?: Record<string, unknown>) => {
    return await request('payments/create-checkout-session', {
        analytics_context: analyticsContext,
        type: type,
        period: period,
        preview: preview,
        discount: discount,
        trial: trial,
        // A code an email offered; the server ignores anything Stripe does not
        // know as an active promotion code.
        ...(promo ? {promo} : {}),
        locale: getLanguage(),
    })
}

export const customerPortalRequest = async () => {
    return await request('payments/create-portal-session', {
        locale: getLanguage(),
    })
}

export const paymentDetailsRequest = async () => {
    return await request("payments/details", {}, "get", true)
}

export const waitForPaymentRequest = async (sessionId: string) => {
    return await request("payments/wait", {"session_id": sessionId}, "post", true)
}

// Pay-first guest checkout: an anonymous user (identified by unique_token) gets
// a Stripe Checkout url directly, before having an account.
export const guestCheckoutSession = async (type: string, period: 'monthly' | 'yearly', trial = false, promo?: string) => {
    return await request('payments/guest-checkout-session', {
        type,
        period,
        ...(promo ? {promo} : {}),
        // Asking is not having: the server decides whether the session really
        // carries the trial and answers with `trial_applied`.
        trial,
        unique_token: getUniqueToken(),
        service: 'app',
        locale: getLanguage(),
    })
}

// Authoritative email of a completed guest checkout session + whether it
// already maps to a registered account (login vs signup mode on return).
export interface GuestCheckoutContext {
    /** Read from the checkout's subscription, not the requested offer. */
    trial?: boolean;
    plan?: string | null;
    payment_confirmed?: boolean;
}

export interface GuestCheckoutSessionInfo extends GuestCheckoutContext {
    email: string | null;
    account_exists: boolean;
    has_active_subscription: boolean;
}

export const guestCheckoutSessionInfo = async (sessionId: string): Promise<GuestCheckoutSessionInfo> => {
    return await request(`payments/checkout-session/${sessionId}?service=app`, {}, "get")
}

// Attach a guest checkout to the already-authenticated caller (logged-in user
// who opened the claim email). Backend verifies the session email matches.
export const claimGuestCheckout = async (sessionId: string): Promise<{subscription: string; billing_reconcile?: AppBillingReconcile | null}> => {
    return await request("payments/claim-guest-checkout", {session_id: sessionId}, "post", true)
}

export const updatePaymentDetailsRequest = async (data: any) => {
    return await request("payments/details", data, "post", true)
}

export const setupAutoPayRequest = async (enabled: boolean, limit: number, addAmount: number) => {
    return await request("payments/setup-auto-pay", {
        enabled: enabled,
        limit: limit,
        add_amount: addAmount
    }, "post", true)
}

export const adminUtil = async (data: object) => {
    return await request("users/util", data, "post", true)
}

/** The one-time scan's price, in its own currency (the default one where the market has no
 *  amount), and the characters it takes per text. */
export type OneTimeScanOffer = {price: number; currency: string; max_chars: number};

export type PlansResponse = {
    plans: any[];
    currency?: string;
    availableCurrencies?: string[];
    oneTimeScan?: OneTimeScanOffer;
    experiments?: ServerArms;
};

// `currency` is the visitor's explicit pick from the switcher; the backend
// honours it only if they were entitled to it anyway, and the currency it
// answers with is the one checkout will charge.
export const getPlans = async (currency?: string): Promise<PlansResponse> => {
    const country = getClientCountry();
    const query = new URLSearchParams({service: 'app'});
    if (currency) query.set('currency', currency);

    const headers: Record<string, string> = {};
    if (country) headers['X-Client-Country'] = country;
    // Prices are public, but the endpoint answers a known caller personally — an
    // account pinned to a currency for testing (adminUtil's setCurrency) must be
    // quoted in the currency it will be charged in. Optional on both sides: no
    // token, or one the backend no longer knows, is simply an anonymous visitor.
    const token = rootStore.user.token;
    if (token) headers['Authorization'] = `Token ${token}`;

    const response = await fetch(`${BACKEND_URL}payments/prices?${query}`, {headers});
    if (!response.ok) return {plans: []};

    const body = await response.json();
    const data = body?.data || body || {};
    return {
        plans: data.plans || [],
        currency: data.currency,
        availableCurrencies: data.available_currencies,
        oneTimeScan: data.one_time_scan,
        experiments: data.experiments,
    };
}

export type ErrorResponse = null | {
    code: string,
    message: string
}

type Status = "success" | "error" | "processing"

interface ScanData {
    text: string
    score: number
}
export interface AIScanData extends ScanData, AiMetrics {
    segmentation_data: Sentence[]
    /** Whether this AI task ran as a deep scan. The mode belongs to the task, so it is the
     *  only honest source for a stored report: the task list can't stand in for it, since
     *  grammar is its own scan type and a deep scan no longer implies one. */
    deep_scan?: boolean
}

export interface PlagiarismScanData extends ScanData, PlagiarismData{
}

export type GrammarSeverity = "error" | "warning" | "style"

export interface GrammarMatch {
  id: string
  offset: number
  length: number
  context: string
  rule_id: string
  category_id: string
  category: string
  issue_type: string
  severity: GrammarSeverity
  short_message: string
  message: string
  replacements: string[]
  url?: string
}

export interface GrammarScanData extends ScanData {
  matches: GrammarMatch[]
  language: string
  stats: { errors: number; warnings: number; style: number; total: number }
}

export interface ScanResponse {
    id: number
    status: Status
    type: "plagiarism" | "ai" | "grammar"
    error: ErrorResponse
    data: AIScanData | PlagiarismScanData | GrammarScanData
}

export interface ReportResponse {
    report_id: string,
    report_url: string,
    items: ScanResponse[],
    date: string,
    /** Whether this report's certificate can be downloaded. The report reads for anyone
     *  holding the link; the certificate follows the plan of the account that ran the
     *  scan, so a free scan has none and a paid one is the author's to share. */
    certificate_available: boolean
}

/**
 * Fetches a completed report by its UUID.
 * The response format matches the result returned by waitTaskResult.
 */
export const getReport = async (reportId: string, signal?: AbortSignal): Promise<ReportResponse> => {
    return await request(`report/${reportId}`, {}, "get", false, signal)
}

export interface MoodleStatisticDataPoint {
    date: string;
    ai_text: number;
    matched_text: number;
}

export interface MoodleStatisticBarData {
    date: string;
    value: number;
}

export interface MoodleStatisticsResponse {
    originality_data: MoodleStatisticDataPoint[];
    total_matched_text: number;
    matched_text_cases: number;
    cheat_detection: number;
    total_scans: number;
    ai_text_cases: number;
    cross_language: number;
    submitted_scans: MoodleStatisticBarData[];
    matched_text_results: MoodleStatisticBarData[];
}

/**
 * Fetches Moodle statistics for a specific UUID.
 * 
 * @param uuid - The unique identifier for the Moodle instance
 * @param signal - Optional AbortSignal for request cancellation
 * @returns Promise containing statistics data including:
 *   - originality_data: Time series data for AI text vs matched text
 *   - Metric values: total matched text, matched cases, cheat detection, etc.
 *   - submitted_scans: Bar chart data for scan submissions over time
 *   - matched_text_results: Bar chart data for matched text results over time
 */
export const getMoodleStatistics = async (uuid: string, signal?: AbortSignal): Promise<MoodleStatisticsResponse> => {
    const response = await request(`moodle/statistics/${uuid}`, {}, "get", false, signal);
    return response.data;
}

/**
 * Records why a subscriber is leaving, from the one-click links in the cancellation email.
 *
 * The signed token from the link identifies the account, so this works for a
 * reader who is not signed in on the device they open mail on — which is most
 * of them. Failures are swallowed: the page thanks them either way.
 */
export const sendCancellationReason = async (reason: string, u: string) => {
    try {
        return await request('mailing/feedback/', {reason, u});
    } catch {
        return null;
    }
}

export const mapAnonUser = async (uuid: string) => {
    return await request('users/map_anon_user/', {uuid})
}

/**
 * Downloads a scan report PDF from the backend.
 * Triggers browser download using the filename provided by the backend in Content-Disposition.
 * Passes user's timezone for correct date/time display in the report, and the UI
 * language — the certificate is rendered server-side, so it can't infer it.
 *
 * The report path, not the API-key one (`report/download/<id>`, which serves the calling
 * account's own scans): a certificate is the author's to share, so this one asks whose
 * scan it is rather than who is downloading — the only question a report opened by its
 * link can answer.
 *
 * The token still goes along when the session has one, and decides nothing: it buys the
 * caller the per-account rate limit instead of the per-address one, which is what makes
 * a batch of "get all certificates" (a request per file) different from a script walking
 * report ids. A report opened by a link simply has no token to send.
 */
export const downloadCertificate = async (reportId: string): Promise<void> => {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const url = `${BACKEND_URL}report/${reportId}/certificate`
        + `?tz=${encodeURIComponent(timezone)}&lang=${encodeURIComponent(getLanguage())}`;
    const token = rootStore.user.token;
    const response = await axios.get(url, {
        responseType: 'blob',
        headers: token ? {'Authorization': 'Token ' + token} : undefined,
    }).catch(async e => {
        // A refusal arrives as a blob too: its JSON body is read back so the error says why.
        const data = axios.isAxiosError(e) ? e.response?.data : undefined;
        if (data instanceof Blob) e.response!.data = await data.text().then(JSON.parse).catch(() => undefined);
        throw e;
    });
    const filename = parseFilenameFromContentDisposition(response.headers['content-disposition']);

    const blob = new Blob([response.data], { type: 'application/pdf' });
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    // Always set `download` so the browser downloads the blob instead of navigating to it
    // (navigation + immediate revoke below would break the in-browser PDF viewer).
    link.download = filename ?? '';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
}

function parseFilenameFromContentDisposition(header: string | undefined): string | null {
    if (!header) return null;
    // Prefer RFC 5987 filename* (supports UTF-8)
    const starMatch = header.match(/filename\*\s*=\s*([^;]+)/i);
    if (starMatch) {
        const value = starMatch[1].trim();
        const parts = value.match(/^([^']*)'([^']*)'(.+)$/);
        if (parts) {
            try {
                return decodeURIComponent(parts[3]);
            } catch {
                return parts[3];
            }
        }
    }
    const match = header.match(/filename\s*=\s*"?([^";]+)"?/i);
    return match ? match[1].trim() : null;
}

// ── Anonymous auth ──────────────────────────────────────────────────

async function hmacSign(data: string, secretKey: string): Promise<string> {
    // crypto.subtle only exists in a secure context (HTTPS / localhost). Over
    // plain HTTP on a LAN IP (e.g. testing on a phone) it's undefined — fall back
    // to a pure-JS HMAC that yields the identical digest so anon auth still works.
    if (typeof crypto === 'undefined' || !crypto.subtle) {
        return hmacSha256Hex(secretKey, data);
    }
    const encoder = new TextEncoder();
    const key = await crypto.subtle.importKey(
        'raw',
        encoder.encode(secretKey),
        { name: 'HMAC', hash: 'SHA-256' },
        false,
        ['sign'],
    );
    const signature = await crypto.subtle.sign('HMAC', key, encoder.encode(data));
    return bytesToHex(new Uint8Array(signature));
}

export interface RequestsInfoResponse {
    total_requests: number;
    today_requests: number;
    anon_max_requests: number;
    max_daily_requests: number;
    token: string;
}

export async function fetchRequestsInfo(): Promise<RequestsInfoResponse> {
    if (!SECRET_KEY) {
        throw new Error('REACT_APP_SECRET_KEY must be set');
    }

    const uniqueToken = getUniqueToken();

    const payload = JSON.stringify({
        timestamp: Date.now() / 1000,
        unique_token: uniqueToken,
        plan: 'Free',
        service: 'app',
    });

    const signature = await hmacSign(payload, SECRET_KEY);

    return await retryWhileUnreachable(async () => requireJson((await axios.post(
        BACKEND_URL + `bare-request/${uniqueToken}/requests-info`,
        { data: payload, signature },
        { timeout: BOOT_TIMEOUT_MS },
    )).data, 'requests-info'));
}

// Hand a text to an app tab opened elsewhere (bitapi/analysis/share_text.py): the token
// rides in `?text_token=`, which init.js reads. It lives for a minute and is read once.
export const shareText = async (text: string): Promise<string> => {
    const data = await request('share-text', {text})
    return data.token
}

// The same for documents already uploaded (bitapi/analysis/share_files.py): `?files_token=`.
export const shareFiles = async (fileUuids: string[]): Promise<string> => {
    const data = await request('share-files', {file_uuids: fileUuids})
    return data.token
}
